import React, { useState } from 'react';
import { liteClient as algoliasearch } from 'algoliasearch/lite';
import { InstantSearch, SearchBox, Hits, Highlight, Configure, useSearchBox } from 'react-instantsearch';

// Check if environment variables are available
const appId = import.meta.env.VITE_ALGOLIA_APP_ID;
const searchKey = import.meta.env.VITE_ALGOLIA_SEARCH_KEY;
const indexName = import.meta.env.VITE_ALGOLIA_INDEX_NAME;

// Debug logging (remove in production)
// console.log('🔍 APP ID:', appId);
// console.log('🔑 SEARCH KEY:', searchKey);
// console.log('📊 INDEX NAME:', indexName);

// Only create search client if env vars are available
const searchClient = appId && searchKey ? algoliasearch(appId, searchKey) : null;

// Test Algolia connection
if (searchClient && indexName) {
  searchClient.search([{
    indexName: indexName,
    params: {
      query: '',
      hitsPerPage: 1
    }
  }])
    .then(result => {
      const indexResult = result.results[0];
      console.log('✅ Algolia connection successful!', {
        indexName,
        totalHits: indexResult.nbHits,
        processingTimeMS: indexResult.processingTimeMS
      });
      if (indexResult.nbHits === 0) {
        console.log('ℹ️ Index is empty - ready for data population');
      }
    })
    .catch(error => {
      console.error('❌ Algolia connection failed:', error);
      console.log('Check your credentials and index name');
    });
}

const Hit = ({ hit }) => {
  const handleClick = (e) => {
    e.preventDefault();
    
    // Parse the URL to get path and hash
    const url = new URL(hit.url, window.location.origin);
    const path = url.pathname;
    const hash = url.hash;
    
    // If we're already on the target page and there's a hash, just scroll to it
    if (window.location.pathname === path && hash) {
      const elementId = hash.substring(1);
      console.log('Looking for element with ID:', elementId);
      const element = document.getElementById(elementId);
      console.log('Found element:', element);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
        // Update URL without causing navigation
        window.history.pushState(null, '', hit.url);
        return;
      } else {
        console.log('Element not found, falling back to full navigation');
      }
    }
    
    // Otherwise, navigate to different page with anchor handling
    if (hash) {
      // For cross-page navigation with anchors, we need special handling
      window.location.href = hit.url;
      // After navigation, we'll need to scroll to the anchor
      // This will be handled by the browser automatically, but we can enhance it
    } else {
      // No anchor, just navigate normally
      window.location.href = hit.url;
    }
  };

  return (
    <a 
      href={hit.url}
      onClick={handleClick}
      className="block p-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0 transition-colors group w-full cursor-pointer"
    >
      <div className="flex items-start justify-between w-full min-w-0">
        <div className="flex-1 min-w-0 pr-2">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <h3 className="text-sm font-semibold text-gray-900 group-hover:text-blue-600 transition-colors truncate">
              <Highlight attribute="title" hit={hit} />
            </h3>
            {hit.type === 'section' && hit.category && (
              <span className="text-xs px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded flex-shrink-0">
                {hit.category}
              </span>
            )}
          </div>
          {hit.page && hit.type === 'section' && (
            <p className="text-xs text-gray-500 mb-1 truncate">in {hit.page}</p>
          )}
          <p className="text-xs text-gray-600 line-clamp-2 break-words">
            <Highlight attribute="content" hit={hit} />
          </p>
        </div>
        <svg className="w-3 h-3 text-gray-400 group-hover:text-blue-600 transition-colors flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </div>
    </a>
  );
};

// Custom search component to control visibility
const CustomSearchResults = () => {
  const { query } = useSearchBox();
  
  if (!query || query.trim().length === 0) {
    return null;
  }

  return (
    <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-80 overflow-y-auto">
      <Hits 
        hitComponent={Hit}
        classNames={{
          root: 'w-full',
          list: 'w-full m-0 p-0 list-none',
          item: 'w-full'
        }}
      />
    </div>
  );
};

const SearchBar = () => {
  const [isFocused, setIsFocused] = useState(false);

  // If Algolia is not configured, show a placeholder
  if (!searchClient || !indexName) {
    return (
      <div className="relative w-full min-w-80 max-w-4xl">
        <input
          type="text"
          placeholder="Search (Algolia not configured)"
          className="w-full p-3 pr-10 border border-gray-300 rounded-lg bg-gray-100 text-gray-500 cursor-not-allowed"
          disabled
        />
      </div>
    );
  }

  return (
    <InstantSearch searchClient={searchClient} indexName={indexName}>
      <Configure hitsPerPage={6} />
      <div className="relative w-full min-w-80 max-w-4xl">
        <div onFocus={() => setIsFocused(true)} onBlur={() => setTimeout(() => setIsFocused(false), 200)}>
          <SearchBox
            placeholder="Search the site..."
            classNames={{
              root: 'search-root',
              form: 'search-form',
              input: 'search-input',
              submit: 'search-submit',
              reset: 'search-reset',
              submitIcon: 'search-submit-icon',
              resetIcon: 'search-reset-icon',
            }}
          />
          {isFocused && <CustomSearchResults />}
        </div>
      </div>
    </InstantSearch>
  );
};

export default SearchBar;
